'use client'

import { useEffect, useState } from 'react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp, Thread } from './demoParts'

// Real estate: speed to lead. A lead arrives from Zillow, Facebook or the
// IDX site; the clock races the page's 60-second promise; a personalised text
// goes out, they reply, and an AI voice call qualifies them and books the
// showing. The NAR "9x within 5 minutes" figure is the page's own.

const SOURCES = {
  zillow: {
    label: 'Zillow',
    lead: 'Dana Brooks · asked about 42 Oak Street',
    text: 'Hi Dana, it’s Jamie with Summit Realty. Saw you asked about 42 Oak Street. Still looking?',
    reply: 'Yes! Could I see it this weekend?',
    booked: 'Showing booked: Sat 11:00 AM',
  },
  facebook: {
    label: 'Facebook ad',
    lead: 'Marcus Lee · "Thinking of selling in spring"',
    text: 'Hi Marcus, it’s Jamie with Summit Realty. Want a free valuation of your home before spring?',
    reply: 'Sure, what do you need from me?',
    booked: 'Valuation call booked: Tue 5:30 PM',
  },
  idx: {
    label: 'Website (IDX)',
    lead: 'Priya Shah · saved 3 homes in Riverside',
    text: 'Hi Priya, it’s Jamie with Summit Realty. Saw you saved 3 homes in Riverside. Want a tour of the best one?',
    reply: 'The one on Elm looks great.',
    booked: 'Tour booked: Sun 2:00 PM',
  },
}
const RESPONSE_SECONDS = 38
const DELAYS = [600, 2600, 1300, 1300, 1200]

export default function SpeedToLead() {
  const [ref, onScreen] = useOnScreen()
  const [source, setSource] = useState('zillow')
  const [run, setRun] = useState(0)
  const s = SOURCES[source]
  const step = useSequence(DELAYS, { run: onScreen, resetKey: `${source}-${run}` })
  const finished = step >= DELAYS.length

  // The clock runs from the lead arriving (step 1) until the text goes (step 2).
  const [secs, setSecs] = useState(0)
  const [clockKey, setClockKey] = useState(`${source}-${run}`)
  if (clockKey !== `${source}-${run}`) { setClockKey(`${source}-${run}`); setSecs(0) }
  useEffect(() => {
    if (step !== 1 || secs >= RESPONSE_SECONDS) return undefined
    const timer = setTimeout(() => setSecs((n) => n + 1), 2500 / RESPONSE_SECONDS)
    return () => clearTimeout(timer)
  }, [step, secs])
  const shownSecs = step >= 2 ? RESPONSE_SECONDS : secs

  const messages = [
    ...(step >= 2 ? [{ from: 'us', text: s.text, meta: `Auto-text · ${RESPONSE_SECONDS}s after the lead arrived` }] : []),
    ...(step >= 3 ? [{ from: 'them', text: s.reply }] : []),
    ...(step >= 4 ? [{ from: 'us', text: 'AI call qualified them: pre-approved, ready to move in 60 days.', meta: 'AI voice call' }] : []),
  ]
  const pick = (key) => { setSource(key); setRun((n) => n + 1) }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-speed"
      title="Speed to lead, every time"
      label="Demo: a real estate lead contacted within seconds and qualified by an AI call"
      tabs={Object.entries(SOURCES).map(([key, v]) => ({ key, label: v.label }))}
      activeTab={source}
      onTab={pick}
      onReplay={() => pick(source)}
    >
      <div className="svd-speed-top">
        <div className="svd-speed-lead">
          <small>New lead from {s.label}</small>
          <strong>{step >= 1 ? s.lead : 'Waiting for a lead…'}</strong>
        </div>
        <div className="svd-speed-clock" aria-label={`Responded in ${shownSecs} seconds of a 60-second target`}>
          <span className="svd-3d-num is-small">0:{String(shownSecs).padStart(2, '0')}</span>
          <small>target 1:00</small>
        </div>
      </div>
      <Thread messages={messages} id={`${source}-${run}`} />
      <p className="svd-stat-line">Contacting a lead within 5 minutes makes them <b>9x</b> more likely to convert (NAR).</p>
      <ResultStamp show={finished} tone="good" text={s.booked} id={`${source}-${run}`} />
    </DemoFrame>
  )
}
